import { useCallback, useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useNavigationGuardContext } from '@/app/context/navigation-guard';

interface PendingNavigation {
  type: 'push' | 'replace' | 'back' | 'link';
  url?: string;
  options?: { scroll?: boolean };
}

interface UseNavigationGuardOptions {
  hasUnsavedChanges: () => boolean;
  onNavigationBlocked?: (url?: string) => void;
}

const globalShowWarningRef: { current: ((nav: PendingNavigation) => void) | null } = {
  current: null,
};
const globalUnsavedStateRef: {
  current: { hasUnsaved: boolean; pathname: string | null };
} = { current: { hasUnsaved: false, pathname: null } };

export function registerGlobalShowWarning(showWarning: (nav: PendingNavigation) => void) {
  globalShowWarningRef.current = showWarning;
}

export function unregisterGlobalShowWarning() {
  globalShowWarningRef.current = null;
}

export function clearGlobalUnsavedState() {
  globalUnsavedStateRef.current = { hasUnsaved: false, pathname: null };
}

export function useNavigationGuard({
  hasUnsavedChanges,
  onNavigationBlocked,
}: UseNavigationGuardOptions) {
  const router = useRouter();
  const pathname = usePathname();
  const navigationGuardContext = useNavigationGuardContext();
  const hasPushedStateRef = useRef(false);
  const hasUnsavedChangesRef = useRef(false);

  const executeNavigation = navigationGuardContext.executePendingNavigation;

  const guardedPush = useCallback(
    (url: string, options?: { scroll?: boolean }) => {
      if (hasUnsavedChanges()) {
        navigationGuardContext.showNavigationWarning({ type: 'push', url, options });
        onNavigationBlocked?.(url);
      } else {
        router.push(url, options);
      }
    },
    [hasUnsavedChanges, router, onNavigationBlocked, navigationGuardContext]
  );

  const guardedReplace = useCallback(
    (url: string, options?: { scroll?: boolean }) => {
      if (hasUnsavedChanges()) {
        navigationGuardContext.showNavigationWarning({ type: 'replace', url, options });
        onNavigationBlocked?.(url);
      } else {
        router.replace(url, options);
      }
    },
    [hasUnsavedChanges, router, onNavigationBlocked, navigationGuardContext]
  );

  const guardedBack = useCallback(() => {
    if (hasUnsavedChanges()) {
      navigationGuardContext.showNavigationWarning({ type: 'back' });
      onNavigationBlocked?.();
    } else {
      router.back();
    }
  }, [hasUnsavedChanges, router, onNavigationBlocked, navigationGuardContext]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      let target = e.target as HTMLElement;
      let anchorElement: HTMLElement | null = null;

      if (target.tagName === 'A' && target.getAttribute('href')) {
        anchorElement = target;
      } else {
        target = target.parentElement as HTMLElement;
        while (target && target !== document.body) {
          if (target.tagName === 'A' && target.getAttribute('href')) {
            anchorElement = target;
            break;
          }
          target = target.parentElement as HTMLElement;
        }
      }

      if (!anchorElement) return;

      const href = anchorElement.getAttribute('href');
      if (!href) return;

      if (
        href.startsWith('http') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('#') ||
        anchorElement.hasAttribute('target') ||
        anchorElement.hasAttribute('download') ||
        e.ctrlKey ||
        e.metaKey ||
        e.shiftKey ||
        e.button !== 0
      ) {
        return;
      }

      if (!href.startsWith('/')) return;

      try {
        const url = new URL(href, window.location.origin);
        const currentUrl = new URL(window.location.href);

        if (url.pathname === currentUrl.pathname) return;

        if (hasUnsavedChanges()) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          navigationGuardContext.showNavigationWarning({ type: 'link', url: href });
          onNavigationBlocked?.(href);
        }
      } catch {
        return;
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [hasUnsavedChanges, onNavigationBlocked, navigationGuardContext]);

  useEffect(() => {
    const hasUnsaved = hasUnsavedChanges();
    hasUnsavedChangesRef.current = hasUnsaved;

    globalUnsavedStateRef.current = {
      hasUnsaved,
      pathname: hasUnsaved ? pathname : null,
    };
  });

  useEffect(() => {
    const hasUnsaved = hasUnsavedChanges();

    if (hasUnsaved && !hasPushedStateRef.current) {
      window.history.pushState({ preventBack: true }, '', window.location.href);
      hasPushedStateRef.current = true;
    }

    if (!hasUnsaved) {
      hasPushedStateRef.current = false;
    }
  }, [hasUnsavedChanges, pathname]);

  useEffect(() => {
    registerGlobalShowWarning(navigationGuardContext.showNavigationWarning);

    return () => {
      unregisterGlobalShowWarning();
    };
  }, [navigationGuardContext]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasUnsavedFromRef = globalUnsavedStateRef.current.hasUnsaved;

      if (hasUnsavedFromRef && hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handleDiscard = () => {
      executeNavigation();
    };

    const handleSave = () => {
      // "Stay" button - do nothing, modal will close
    };

    navigationGuardContext.setHandlers(handleDiscard, handleSave);

    return () => {
      navigationGuardContext.setHandlers(undefined, undefined);
    };
  }, [navigationGuardContext, executeNavigation]);

  return {
    isModalOpen: navigationGuardContext.isModalOpen,
    setIsModalOpen: navigationGuardContext.setModalOpen,
    pendingNavigation: navigationGuardContext.pendingNavigation,
    guardedPush,
    guardedReplace,
    guardedBack,
    handleDiscard: () => navigationGuardContext.executePendingNavigation(),
    handleSave: () => navigationGuardContext.setModalOpen(false),
    executeNavigation,
  };
}
